import React, { useState, Component } from 'react';
import { Badge } from '../components/SubscriptionAppBadge';
import { Button } from '../components/SubscriptionAppButton';
import { IconButton } from '../components/SubscriptionAppIconButton';
import { Plus, Truck, Percent, Wine, GlassWater, Star, CreditCard, X, Upload, ChevronDown } from 'lucide-react';
import { NavActions } from '../components/NavBar/NavActions';
import { Tabs } from '../components/Tabs';
import { Input } from '../components/SubscriptionAppInput';
// Icon config per reward type
const rewardIconConfig: Record<string, {
  icon: React.FC<{
    className?: string;
  }>;
  bg: string;
  color: string;
}> = {
  Shipping: {
    icon: Truck,
    bg: 'bg-[#DBEAFE]',
    color: 'text-[#3B82F6]'
  },
  Discount: {
    icon: Percent,
    bg: 'bg-[#D1FAE5]',
    color: 'text-[#10B981]'
  },
  Product: {
    icon: Wine,
    bg: 'bg-[#FEF3C7]',
    color: 'text-[#F59E0B]'
  },
  Experience: {
    icon: GlassWater,
    bg: 'bg-[#EDE9FE]',
    color: 'text-[#6F42C1]'
  },
  'Tier Up': {
    icon: Star,
    bg: 'bg-[#FEF3C7]',
    color: 'text-[#F59E0B]'
  },
  Credit: {
    icon: CreditCard,
    bg: 'bg-[#D1FAE5]',
    color: 'text-[#10B981]'
  }
};
// Mock Data
const mockRewards = [{
  id: '1',
  name: 'Free Shipping',
  type: 'Shipping',
  points: 250,
  redeemed: 342,
  stock: '∞',
  minTier: 'Any',
  status: 'Active'
}, {
  id: '2',
  name: '$10 Off Next Order',
  type: 'Discount',
  points: 1000,
  redeemed: 218,
  stock: '∞',
  minTier: 'Any',
  status: 'Active'
}, {
  id: '3',
  name: 'Free Rosé Bottle',
  type: 'Product',
  points: 1500,
  redeemed: 89,
  stock: '23 remaining',
  minTier: 'Any',
  status: 'Active'
}, {
  id: '4',
  name: 'Reserve Tasting for Two',
  type: 'Experience',
  points: 2500,
  redeemed: 56,
  stock: '50 remaining',
  minTier: 'Connoisseur',
  status: 'Active'
}, {
  id: '5',
  name: 'Gold Status for 1 Month',
  type: 'Tier Up',
  points: 3000,
  redeemed: 12,
  stock: '∞',
  minTier: 'Any',
  status: 'Active'
}, {
  id: '6',
  name: '$5 Account Credit',
  type: 'Credit',
  points: 500,
  redeemed: 0,
  stock: '∞',
  minTier: 'Any',
  status: 'Draft'
}];
export function LoyaltyRewardsPage() {
  const [activeTab, setActiveTab] = useState('All');
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [editingReward, setEditingReward] = useState<(typeof mockRewards)[0] | null>(null);
  const [rewardType, setRewardType] = useState('Discount Code');
  const [rewardName, setRewardName] = useState('10% Off Next Order');
  const [rewardDescription, setRewardDescription] = useState('Get 10% off your next wine order');
  const [rewardPoints, setRewardPoints] = useState('1000');
  const [discountType, setDiscountType] = useState<'flat' | 'percentage'>('percentage');
  const [toggles, setToggles] = useState({
    inventoryLimit: false,
    perCustomerLimit: false,
    cooldown: false,
    dateRange: false,
    minimumTier: true,
    minimumTenure: false
  });
  const [minTier, setMinTier] = useState('Connoisseur');
  // Map table type to drawer select type
  const typeToSelectMap: Record<string, string> = {
    Shipping: 'Free Shipping',
    Discount: 'Discount Code',
    Product: 'Free Product',
    Experience: 'Experience',
    'Tier Up': 'Tier Upgrade',
    Credit: 'Account Credit'
  };
  const openCreateDrawer = () => {
    setEditingReward(null);
    setRewardType('Discount Code');
    setRewardName('');
    setRewardDescription('');
    setRewardPoints('');
    setDiscountType('percentage');
    setToggles({
      inventoryLimit: false,
      perCustomerLimit: false,
      cooldown: false,
      dateRange: false,
      minimumTier: false,
      minimumTenure: false
    });
    setMinTier('Connoisseur');
    setIsDrawerOpen(true);
  };
  const openEditDrawer = (reward: (typeof mockRewards)[0]) => {
    setEditingReward(reward);
    setRewardType(typeToSelectMap[reward.type] || 'Discount Code');
    setRewardName(reward.name);
    setRewardDescription(`${reward.name} reward`);
    setRewardPoints(String(reward.points));
    setDiscountType('percentage');
    const hasTier = reward.minTier !== 'Any';
    setToggles({
      inventoryLimit: false,
      perCustomerLimit: false,
      cooldown: false,
      dateRange: false,
      minimumTier: hasTier,
      minimumTenure: false
    });
    setMinTier(hasTier ? reward.minTier : 'Connoisseur');
    setIsDrawerOpen(true);
  };
  const closeDrawer = () => {
    setIsDrawerOpen(false);
    setEditingReward(null);
  };
  const tabs = [{
    id: 'All',
    label: 'All'
  }, {
    id: 'Active',
    label: 'Active'
  }, {
    id: 'Draft',
    label: 'Draft'
  }, {
    id: 'Archived',
    label: 'Archived'
  }];
  const filteredRewards = mockRewards.filter(reward => {
    if (activeTab === 'All') return true;
    return reward.status === activeTab;
  });
  const getTypeBadgeVariant = (type: string) => {
    switch (type) {
      case 'Shipping':
        return 'info';
      case 'Discount':
        return 'success';
      case 'Product':
        return 'warning';
      case 'Experience':
        return 'default';
      // Purple isn't standard, using default or maybe info
      case 'Tier Up':
        return 'warning';
      case 'Credit':
        return 'success';
      default:
        return 'default';
    }
  };
  const getStatusBadgeVariant = (status: string) => {
    switch (status) {
      case 'Active':
        return 'success';
      case 'Draft':
        return 'default';
      // Grey
      case 'Archived':
        return 'default';
      // Muted/Grey
      default:
        return 'default';
    }
  };
  const getMinTierBadgeVariant = (tier: string) => {
    switch (tier) {
      case 'Any':
        return 'default';
      case 'Connoisseur':
        return 'info';
      default:
        return 'default';
    }
  };
  const handleToggle = (key: keyof typeof toggles) => {
    setToggles(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };
  return <div className="bg-[#F1F5F9] min-h-full p-6 relative" data-id="element-6881">
      {/* Header */}
      <div className="mb-6" data-id="element-6882">
        <div className="w-full h-16 bg-white border-b border-[#CBD5E1] px-6 flex items-center gap-4" data-id="element-6883">
          <h1 className="text-lg font-semibold text-[#0F172A] m-0" data-id="element-6884">Rewards</h1>
          <div className="flex-1" data-id="element-6885" />
          <Button variant="primary" size="md" onClick={openCreateDrawer} iconLeft={<Plus className="w-4 h-4" data-id="element-6887" />} data-id="element-6886">
            Create Reward
          </Button>
          <NavActions data-id="element-6888" />
        </div>
      </div>

      {/* Tabs */}
      <div className="mb-6" data-id="element-6889">
        <Tabs tabs={tabs} activeTab={activeTab} onTabChange={setActiveTab} data-id="element-6890" />
      </div>

      {/* Data Table */}
      <div className="bg-white border border-ocean-border-subtle rounded-lg shadow-sm overflow-hidden" data-id="element-6891">
        <div className="overflow-x-auto" data-id="element-6892">
          <table className="w-full" data-id="element-6893">
            <thead className="bg-ocean-gray-50 border-b border-ocean-border-subtle" data-id="element-6894">
              <tr data-id="element-6895">
                <th className="px-6 py-3 text-left text-xs font-semibold text-ocean-text-muted uppercase tracking-wider" data-id="element-6896">
                  Reward
                </th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-ocean-text-muted uppercase tracking-wider" data-id="element-6897">
                  Type
                </th>
                <th className="px-6 py-3 text-right text-xs font-semibold text-ocean-text-muted uppercase tracking-wider" data-id="element-6898">
                  Points
                </th>
                <th className="px-6 py-3 text-right text-xs font-semibold text-ocean-text-muted uppercase tracking-wider" data-id="element-6899">
                  Redeemed
                </th>
                <th className="px-6 py-3 text-right text-xs font-semibold text-ocean-text-muted uppercase tracking-wider" data-id="element-6900">
                  Stock
                </th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-ocean-text-muted uppercase tracking-wider" data-id="element-6901">
                  Min Tier
                </th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-ocean-text-muted uppercase tracking-wider" data-id="element-6902">
                  Status
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-ocean-border-subtle" data-id="element-6903">
              {filteredRewards.map(reward => <tr key={reward.id} onClick={() => openEditDrawer(reward)} className="hover:bg-ocean-gray-50 transition-colors cursor-pointer" data-id="element-6904">
                  <td className="px-6 py-4" data-id="element-6905">
                    <div className="flex items-center gap-3" data-id="element-6906">
                      {(() => {
                    const config = rewardIconConfig[reward.type];
                    if (config) {
                      const IconComponent = config.icon;
                      return <div className={`w-8 h-8 rounded-lg ${config.bg} flex items-center justify-center flex-shrink-0`} data-id="element-6907">
                              <IconComponent className={`w-4 h-4 ${config.color}`} data-id="element-6908" />
                            </div>;
                    }
                    return null;
                  })()}
                      <span className="text-sm font-medium text-ocean-text-primary" data-id="element-6909">
                        {reward.name}
                      </span>
                    </div>
                  </td>
                  <td className="px-6 py-4" data-id="element-6910">
                    <Badge variant={getTypeBadgeVariant(reward.type)} data-id="element-6911">
                      {reward.type}
                    </Badge>
                  </td>
                  <td className="px-6 py-4 text-right text-sm font-medium text-ocean-text-primary" data-id="element-6912">
                    {reward.points.toLocaleString()}
                  </td>
                  <td className="px-6 py-4 text-right text-sm text-ocean-text-secondary" data-id="element-6913">
                    {reward.redeemed.toLocaleString()}
                  </td>
                  <td className="px-6 py-4 text-right text-sm text-ocean-text-secondary" data-id="element-6914">
                    {reward.stock}
                  </td>
                  <td className="px-6 py-4" data-id="element-6915">
                    <Badge variant={getMinTierBadgeVariant(reward.minTier)} data-id="element-6916">
                      {reward.minTier}
                    </Badge>
                  </td>
                  <td className="px-6 py-4" data-id="element-6917">
                    <Badge variant={getStatusBadgeVariant(reward.status)} data-id="element-6918">
                      {reward.status}
                    </Badge>
                  </td>
                </tr>)}
            </tbody>
          </table>
        </div>
      </div>

      {/* Drawer Overlay */}
      {isDrawerOpen && <div className="fixed inset-0 bg-black/50 z-50 transition-opacity duration-300" onClick={closeDrawer} data-id="element-6919" />}

      {/* Create/Edit Reward Drawer Panel */}
      <div className={`fixed top-0 right-0 h-full w-[480px] bg-white shadow-xl z-50 transform transition-transform duration-300 ease-in-out flex flex-col ${isDrawerOpen ? 'translate-x-0' : 'translate-x-full'}`} data-id="element-6920">
        {/* Drawer Header */}
        <div className="px-6 py-4 border-b border-[#E2E8F0] flex items-center justify-between flex-shrink-0" data-id="element-6921">
          <h2 className="text-lg font-semibold text-[#0F172A]" data-id="element-6922">
            {editingReward ? 'Edit Reward' : 'Create Reward'}
          </h2>
          <IconButton icon={<X className="w-5 h-5" data-id="element-6924" />} label="Close drawer" variant="ghost" onClick={closeDrawer} data-id="element-6923" />
        </div>

        {/* Drawer Content */}
        <div className="flex-1 overflow-y-auto px-6 py-6 space-y-8" data-id="element-6925">
          {/* Section 1: Reward Basics */}
          <section data-id="element-6926">
            <h3 className="text-sm font-semibold text-[#475569] uppercase tracking-wider mb-4" data-id="element-6927">
              Reward Basics
            </h3>
            <div className="space-y-4" data-id="element-6928">
              <div data-id="element-6929">
                <label className="block text-xs font-medium text-[#475569] mb-1.5" data-id="element-6930">
                  Type
                </label>
                <div className="relative" data-id="element-6931">
                  <select value={rewardType} onChange={e => setRewardType(e.target.value)} className="w-full h-10 pl-3 pr-8 border border-[#CBD5E1] rounded-md text-sm text-[#0F172A] focus:border-[#0EA5E9] focus:ring-1 focus:ring-[#0EA5E9] focus:outline-none appearance-none bg-white" data-id="element-6932">
                    <option data-id="element-6933">Discount Code</option>
                    <option data-id="element-6934">Free Shipping</option>
                    <option data-id="element-6935">Free Product</option>
                    <option data-id="element-6936">Experience</option>
                    <option data-id="element-6937">Account Credit</option>
                    <option data-id="element-6938">Tier Upgrade</option>
                    <option data-id="element-6939">Exclusive Access</option>
                  </select>
                  <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#94A3B8] pointer-events-none" data-id="element-6940" />
                </div>
              </div>

              <Input type="text" label="Name" value={rewardName} onChange={e => setRewardName(e.target.value)} placeholder="Enter reward name" data-id="element-6941" />

              <div data-id="element-6942">
                <label className="block text-xs font-medium text-[#475569] mb-1.5" data-id="element-6943">
                  Description
                </label>
                <textarea rows={2} value={rewardDescription} onChange={e => setRewardDescription(e.target.value)} placeholder="Describe this reward" className="w-full px-3 py-2 border border-[#CBD5E1] rounded-md text-sm text-[#0F172A] focus:border-[#0EA5E9] focus:ring-1 focus:ring-[#0EA5E9] focus:outline-none resize-none" data-id="element-6944" />
              </div>

              <Input type="number" label="Points Required" value={rewardPoints} onChange={e => setRewardPoints(e.target.value)} placeholder="0" data-id="element-6945" />

              <div data-id="element-6946">
                <label className="block text-xs font-medium text-[#475569] mb-1.5" data-id="element-6947">
                  Image
                </label>
                <div className="h-20 border-2 border-dashed border-[#CBD5E1] rounded-lg bg-[#F8FAFC] flex flex-col items-center justify-center cursor-pointer hover:bg-[#F1F5F9] transition-colors" data-id="element-6948">
                  <Upload className="w-5 h-5 text-[#94A3B8] mb-1" data-id="element-6949" />
                  <span className="text-xs text-[#94A3B8]" data-id="element-6950">Upload image</span>
                </div>
              </div>
            </div>
          </section>

          {/* Section 2: Type-Specific Details */}
          {rewardType === 'Discount Code' && <section data-id="element-6951">
              <h3 className="text-sm font-semibold text-[#475569] uppercase tracking-wider mb-4" data-id="element-6952">
                Discount Details
              </h3>
              <div className="space-y-4" data-id="element-6953">
                <div className="flex items-center gap-6" data-id="element-6954">
                  <label className="flex items-center gap-2 cursor-pointer" data-id="element-6955">
                    <div className={`w-4 h-4 rounded-full border flex items-center justify-center ${discountType === 'flat' ? 'border-[#0EA5E9]' : 'border-[#CBD5E1]'}`} onClick={() => setDiscountType('flat')} data-id="element-6956">
                      {discountType === 'flat' && <div className="w-2 h-2 rounded-full bg-[#0EA5E9]" data-id="element-6957" />}
                    </div>
                    <span className="text-sm text-[#0F172A]" data-id="element-6958">Flat amount</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer" data-id="element-6959">
                    <div className={`w-4 h-4 rounded-full border flex items-center justify-center ${discountType === 'percentage' ? 'border-[#0EA5E9]' : 'border-[#CBD5E1]'}`} onClick={() => setDiscountType('percentage')} data-id="element-6960">
                      {discountType === 'percentage' && <div className="w-2 h-2 rounded-full bg-[#0EA5E9]" data-id="element-6961" />}
                    </div>
                    <span className="text-sm text-[#0F172A]" data-id="element-6962">Percentage</span>
                  </label>
                </div>

                <div data-id="element-6963">
                  <label className="block text-xs font-medium text-[#475569] mb-1.5" data-id="element-6964">
                    Value
                  </label>
                  <div className="relative" data-id="element-6965">
                    <input type="number" defaultValue="10" className="w-full h-10 pl-3 pr-8 border border-[#CBD5E1] rounded-md text-sm text-[#0F172A] focus:border-[#0EA5E9] focus:ring-1 focus:ring-[#0EA5E9] focus:outline-none" data-id="element-6966" />
                    <div className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-[#64748B]" data-id="element-6967">
                      {discountType === 'percentage' ? '%' : '$'}
                    </div>
                  </div>
                </div>

                <div data-id="element-6968">
                  <label className="block text-xs font-medium text-[#475569] mb-1.5" data-id="element-6969">
                    Applies to
                  </label>
                  <div className="relative" data-id="element-6970">
                    <select className="w-full h-10 pl-3 pr-8 border border-[#CBD5E1] rounded-md text-sm text-[#0F172A] focus:border-[#0EA5E9] focus:ring-1 focus:ring-[#0EA5E9] focus:outline-none appearance-none bg-white" data-id="element-6971">
                      <option data-id="element-6972">Entire order</option>
                      <option data-id="element-6973">Specific products</option>
                      <option data-id="element-6974">Specific collections</option>
                    </select>
                    <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#94A3B8] pointer-events-none" data-id="element-6975" />
                  </div>
                </div>
              </div>
            </section>}

          {/* Section 3: Availability Rules */}
          <section data-id="element-6976">
            <h3 className="text-sm font-semibold text-[#475569] uppercase tracking-wider mb-4" data-id="element-6977">
              Availability Rules
            </h3>
            <div className="space-y-4" data-id="element-6978">
              {[{
              key: 'inventoryLimit',
              label: 'Inventory Limit'
            }, {
              key: 'perCustomerLimit',
              label: 'Per-Customer Limit'
            }, {
              key: 'cooldown',
              label: 'Cooldown'
            }, {
              key: 'dateRange',
              label: 'Date Range'
            }, {
              key: 'minimumTier',
              label: 'Minimum Tier'
            }, {
              key: 'minimumTenure',
              label: 'Minimum Tenure'
            }].map(rule => <div key={rule.key} className="flex flex-col" data-id="element-6979">
                  <div className="flex items-center justify-between" data-id="element-6980">
                    <span className="text-sm font-medium text-[#0F172A]" data-id="element-6981">
                      {rule.label}
                    </span>
                    <button onClick={() => handleToggle(rule.key as keyof typeof toggles)} className={`w-11 h-6 rounded-full transition-colors duration-200 ease-in-out relative ${toggles[rule.key as keyof typeof toggles] ? 'bg-[#0EA5E9]' : 'bg-[#CBD5E1]'}`} data-id="element-6982">
                      <div className={`absolute top-1 left-1 w-4 h-4 bg-white rounded-full shadow-sm transition-transform duration-200 ease-in-out ${toggles[rule.key as keyof typeof toggles] ? 'translate-x-5' : 'translate-x-0'}`} data-id="element-6983" />
                    </button>
                  </div>

                  {/* Expanded Config for Minimum Tier */}
                  {rule.key === 'minimumTier' && toggles.minimumTier && <div className="mt-3 animate-ocean-slide-up" data-id="element-6984">
                      <div className="relative" data-id="element-6985">
                        <select value={minTier} onChange={e => setMinTier(e.target.value)} className="w-full h-10 pl-3 pr-8 border border-[#CBD5E1] rounded-md text-sm text-[#0F172A] focus:border-[#0EA5E9] focus:ring-1 focus:ring-[#0EA5E9] focus:outline-none appearance-none bg-white" data-id="element-6986">
                          <option data-id="element-6987">Enthusiast</option>
                          <option data-id="element-6988">Connoisseur</option>
                          <option data-id="element-6989">Sommelier</option>
                          <option data-id="element-6990">Vintner</option>
                        </select>
                        <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#94A3B8] pointer-events-none" data-id="element-6991" />
                      </div>
                    </div>}
                </div>)}
            </div>
          </section>
        </div>

        {/* Drawer Footer */}
        <div className="px-6 py-4 border-t border-[#E2E8F0] bg-white flex items-center justify-end gap-3 flex-shrink-0" data-id="element-6992">
          <Button variant="ghost" size="md" onClick={closeDrawer} data-id="element-6993">
            Cancel
          </Button>
          <Button variant="primary" size="md" onClick={closeDrawer} data-id="element-6994">
            Save Reward
          </Button>
        </div>
      </div>
    </div>;
}